import React, { useState, useEffect } from "react";
import { X, CheckCircle, Package, Edit3, AlertTriangle } from "lucide-react";

export default function SkuConfirmationModal({ isOpen, items, onClose, onConfirm }) {
  const [editedSkus, setEditedSkus] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize edited SKUs when items change or modal opens
  useEffect(() => {
    if (items && items.length > 0) {
      const initial = {};
      items.forEach(item => {
        initial[item.id] = item.sku ? item.sku.trim().toUpperCase() : "";
      });
      setEditedSkus(initial);
    }
  }, [items, isOpen]);

  if (!isOpen || !items || items.length === 0) return null;

  const handleSkuChange = (id, val) => {
    setEditedSkus(prev => ({
      ...prev,
      [id]: val.toUpperCase()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that no SKU is empty
    const updatedItems = [];
    for (const item of items) {
      const sku = editedSkus[item.id]?.trim();
      if (!sku) {
        alert(`O item "${item.name || 'Sem nome'}" precisa de um SKU válido.`);
        return;
      }
      updatedItems.push({
        id: item.id,
        sku: sku
      });
    }

    setLoading(true);
    try {
      await onConfirm(updatedItems);
    } catch (err) {
      console.error(err);
      alert("Erro ao confirmar SKUs: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-slate-150 animate-scale-in max-h-[85vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-indigo-800 p-4 flex justify-between items-center text-white shrink-0">
          <div className="flex items-center gap-2.5">
            <Package size={20} className="text-purple-200" />
            <div>
              <h3 className="font-black text-sm tracking-wide uppercase">Confirmar SKUs para Estoque</h3>
              <p className="text-[10px] text-purple-200 font-bold">
                Revise ou edite os SKUs antes de enviar as peças para o estoque real
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="hover:bg-white/10 p-1.5 rounded-lg transition-colors text-white disabled:opacity-30"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-5 overflow-y-auto space-y-4 flex-1 custom-scrollbar bg-slate-50/50">
            {items.length > 1 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2.5 text-xs text-amber-800 font-bold">
                <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
                <span>
                  Você está enviando {items.length} itens em massa. Verifique o SKU individual de cada lote abaixo.
                </span>
              </div>
            )}

            <div className="space-y-3.5">
              {items.map((item) => {
                const currentVal = editedSkus[item.id] ?? "";
                const isChanged = currentVal !== (item.sku || "");
                
                return (
                  <div
                    key={item.id}
                    className={`bg-white p-4 rounded-xl border transition-all flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 ${
                      isChanged
                        ? "border-amber-300 bg-amber-50/10 shadow-sm"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800 text-xs truncate block max-w-xs">
                          {item.name || "Sem nome cadastrado"}
                        </span>
                        {isChanged && (
                          <span className="bg-amber-100 text-amber-800 text-[9px] font-black px-1.5 py-0.5 rounded border border-amber-200">
                            Modificado
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500 font-mono mt-1">
                        {item.loteCodigo || item.lote ? (
                          <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-black border border-purple-200">
                            LOTE: {item.loteCodigo || item.lote}
                          </span>
                        ) : null}
                        <span>ID Peça: #{item.id}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
                      <div className="relative w-full sm:w-48">
                        <Edit3 className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                        <input
                          type="text"
                          required
                          placeholder="SKU do Item"
                          disabled={loading}
                          value={currentVal}
                          onChange={(e) => handleSkuChange(item.id, e.target.value)}
                          className="w-full pl-8 pr-2.5 py-2 border border-slate-300 rounded-lg text-xs font-mono font-bold uppercase outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-slate-800 shadow-inner"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t bg-white flex gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2.5 border border-slate-300 font-bold text-xs uppercase tracking-wider text-slate-500 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-850 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 transition-all shadow-md shadow-emerald-100 hover:shadow-lg disabled:opacity-50"
            >
              {loading ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <CheckCircle size={16} /> Confirmar e Enviar ao Estoque
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
